import type { ConfigType } from '@plone/registry';
import './blocks/legacyBlocks.css';
import './blocks/Accordion/Accordion.css';
import './blocks/Columns/Columns.css';
import './blocks/Tabs/Tabs.css';
import accordionSVG from '@plone/volto/icons/list-bullet.svg';
import columnsSVG from '@plone/volto/icons/grid-block.svg';
import tabsSVG from '@plone/volto/icons/nav.svg';
import { LinkElement } from '@plone/volto-slate/editor/plugins/AdvancedLink/render';
import { CalloutElement } from '@plone/volto-slate/editor/plugins/Callout';
import AccordionView from './blocks/Accordion/View';
import ColumnsView from './blocks/Columns/View';
import TabsView from './blocks/Tabs/View';
import readOnlyEdit from './blocks/ReadOnlyEdit';

/**
 * @dox/archives-ui -- UI this archive owns, starting with the renderers for
 * the container blocks the migrated content still holds.
 *
 * Volto 19.1.5 ships no view for `accordion`, `columnsBlock` or `tabs_block`:
 * they came from three separate Volto-17-era addons on the previous site. Six
 * private archive pages carry one, and without a view each served 200 with a
 * complete page frame and no body (BL-0147).
 *
 * Re-registering the three original addons was the alternative. It was
 * rejected: each brings a schema, a sidebar and an edit surface we do not
 * want, plus a dependency to maintain against 19.1.5 -- to render six block
 * instances on pages nobody authors. What was actually missing is view
 * components, so that is what this package is.
 *
 * All three are `restricted`, so the block chooser never offers them. We are
 * not creating new container blocks; we are reading the ones we inherited.
 */
const LEGACY_CONTAINER_BLOCKS = [
  {
    id: 'accordion',
    title: 'Accordion (legacy)',
    icon: accordionSVG,
    view: AccordionView,
  },
  {
    id: 'columnsBlock',
    title: 'Columns (legacy)',
    icon: columnsSVG,
    view: ColumnsView,
  },
  {
    id: 'tabs_block',
    title: 'Tabs (legacy)',
    icon: tabsSVG,
    view: TabsView,
  },
];

/**
 * Slate element types the archive's stored content uses and volto-slate 19.1.5
 * does not register.
 *
 * `a` is the big one. volto-slate's default plugin list installs `Link` (the
 * `link` element) but NOT `AdvancedLink` (the `a` element) -- and `a` is what
 * every link in this archive is stored as, both in the content inherited from
 * the previous site and in everything the draftjs conversion emitted. An
 * unregistered element is not an error in volto-slate: `Element` falls back to
 * `elements.default`, which is a bare `<p>`. So every link rendered as its own
 * text inside a stray paragraph, nested illegally inside the surrounding one:
 *
 *   <p>The following link will assist you in browsing the <p>new portal.</p></p>
 *
 * Measured on the live site 2026-09-24, before this registration: zero anchors
 * in the body of any converted page.
 *
 * `callout2` is the previous site's second callout style. Its one surviving
 * instance holds an empty in-page scroll anchor, so it renders nothing
 * visible -- but registering it means the archive has no unregistered slate
 * element left, which is a property worth being able to assert.
 */
const LEGACY_SLATE_ELEMENTS = {
  a: LinkElement,
  callout2: CalloutElement,
};

function applyConfig(config: ConfigType) {
  const { slate } = config.settings as any;
  slate.elements = { ...slate.elements, ...LEGACY_SLATE_ELEMENTS };
  // `a` is inline: it belongs inside a paragraph, not beside one. The view
  // path does not consult this list, but the editor does, and leaving it out
  // would make a link a block the moment anyone opened one of these pages.
  slate.inlineElements = [...new Set([...(slate.inlineElements || []), 'a'])];

  LEGACY_CONTAINER_BLOCKS.forEach(({ id, title, icon, view }) => {
    (config.blocks.blocksConfig as any)[id] = {
      id,
      title,
      icon,
      group: 'text',
      view,
      edit: readOnlyEdit(view),
      restricted: true,
      mostUsed: false,
      sidebarTab: 0,
      // The children are rendered by the view itself, not by the block
      // wrapper, so Volto must not try to manage focus inside them.
      blockHasOwnFocusManagement: true,
    };
  });

  return config;
}

export { LEGACY_SLATE_ELEMENTS };
export default applyConfig;
